/**
 * 线索付费节点进度展示和管理组件
 * 显示付费节点的整体进度、时间线，并支持节点管理
 */
import React, { useState } from 'react';
import { 
  Card, 
  Progress, 
  Row, 
  Col, 
  Statistic, 
  Timeline, 
  Tag, 
  Typography, 
  Space, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  DatePicker, 
  message,
  Popconfirm,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { MilestoneStatus } from '../../types/financeConfig';
import type { 
  PaymentMilestone, 
  PaymentMilestoneProgress,
  LeadPaymentMilestoneCreate,
  PaymentMilestoneUpdate,
} from '../../types/financeConfig';
import {
  createLeadPaymentMilestone,
  updateLeadPaymentMilestone,
  deleteLeadPaymentMilestone,
} from '../../services/financeConfig';

const { Text } = Typography;
const { TextArea } = Input;

interface LeadMilestoneProgressProps {
  leadId: number;
  milestones: PaymentMilestone[];
  progress: PaymentMilestoneProgress;
  contractAmount: number;
  canEdit: boolean;
  onRefresh: () => void;
}

const LeadMilestoneProgress: React.FC<LeadMilestoneProgressProps> = ({
  leadId,
  milestones,
  progress,
  contractAmount,
  canEdit,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<PaymentMilestone | null>(null);
  const [loading, setLoading] = useState(false);

  // 格式化金额
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // 打开添加/编辑弹窗
  const handleOpenModal = (milestone?: PaymentMilestone) => {
    if (milestone) {
      setEditingMilestone(milestone);
      form.setFieldsValue({
        milestone_name: milestone.milestone_name,
        milestone_order: milestone.milestone_order,
        payment_amount: Number(milestone.payment_amount),
        trigger_condition: milestone.trigger_condition,
        due_date: milestone.due_date ? dayjs(milestone.due_date) : undefined,
        notes: milestone.notes,
      });
    } else {
      setEditingMilestone(null);
      form.setFieldsValue({
        milestone_order: milestones.length + 1,
      });
    }
    setModalVisible(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingMilestone(null);
    form.resetFields();
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingMilestone) {
        // 更新节点
        const updateData: PaymentMilestoneUpdate = {
          milestone_name: values.milestone_name,
          milestone_order: values.milestone_order,
          payment_amount: values.payment_amount,
          trigger_condition: values.trigger_condition,
          due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
          notes: values.notes,
        };
        await updateLeadPaymentMilestone(leadId, editingMilestone.id, updateData);
        message.success('付费节点更新成功');
      } else {
        // 创建节点
        const createData: LeadPaymentMilestoneCreate = {
          lead_id: leadId,
          milestone_name: values.milestone_name,
          milestone_order: values.milestone_order,
          payment_amount: values.payment_amount,
          trigger_condition: values.trigger_condition,
          due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : undefined,
          notes: values.notes,
        };
        await createLeadPaymentMilestone(leadId, createData);
        message.success('付费节点创建成功');
      }

      handleCloseModal();
      onRefresh();
    } catch (error: any) {
      console.error('Failed to save milestone:', error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除节点
  const handleDelete = async (milestone: PaymentMilestone) => {
    try {
      await deleteLeadPaymentMilestone(leadId, milestone.id);
      message.success('付费节点已删除');
      onRefresh();
    } catch (error: any) {
      console.error('Failed to delete milestone:', error);
      message.error(error.message || '删除失败，请重试');
    }
  };

  // 获取状态图标和颜色
  const getStatusConfig = (status: MilestoneStatus) => {
    const configs = {
      [MilestoneStatus.PAID]: {
        icon: <CheckCircleOutlined />,
        color: '#52c41a',
        text: '已收',
      },
      [MilestoneStatus.PENDING]: {
        icon: <ClockCircleOutlined />,
        color: '#faad14',
        text: '待收',
      },
      [MilestoneStatus.OVERDUE]: {
        icon: <ExclamationCircleOutlined />,
        color: '#ff4d4f',
        text: '逾期',
      },
      [MilestoneStatus.CANCELLED]: {
        icon: <CloseCircleOutlined />,
        color: '#d9d9d9',
        text: '已取消',
      },
    };
    return configs[status];
  };

  // 渲染时间线项
  const renderTimelineItems = () => {
    return milestones
      .sort((a, b) => a.milestone_order - b.milestone_order)
      .map((milestone) => {
        const statusConfig = getStatusConfig(milestone.status);
        const isPaid = milestone.status === MilestoneStatus.PAID;
        const isOverdue = milestone.status === MilestoneStatus.OVERDUE;
        const actualAmount = Number(milestone.actual_paid_amount || 0);
        const paymentAmount = Number(milestone.payment_amount);

        return (
          <Timeline.Item
            key={milestone.id}
            dot={statusConfig.icon}
            color={statusConfig.color}
          >
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                  <Text strong>{milestone.milestone_name}</Text>
                  <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                </Space>
                {canEdit && (
                  <Space size="small">
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleOpenModal(milestone)}
                    >
                      编辑
                    </Button>
                    <Popconfirm
                      title="确认删除"
                      description="确定要删除这个付费节点吗？节点将被软删除，关联的财务记录会保留历史关联。"
                      onConfirm={() => handleDelete(milestone)}
                      okText="确认"
                      cancelText="取消"
                      icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                    >
                      <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                      >
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                )}
              </Space>

              <Space size="large">
                <Text>
                  应收：<Text strong>¥{formatAmount(paymentAmount)}</Text>
                </Text>
                {actualAmount > 0 && (
                  <Text>
                    实收：<Text strong style={{ color: '#52c41a' }}>
                      ¥{formatAmount(actualAmount)}
                    </Text>
                  </Text>
                )}
              </Space>

              {milestone.due_date && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isOverdue ? '逾期日期' : isPaid ? '完成日期' : '预计日期'}：
                  {isPaid && milestone.paid_date
                    ? dayjs(milestone.paid_date).format('YYYY-MM-DD')
                    : dayjs(milestone.due_date).format('YYYY-MM-DD')}
                </Text>
              )}

              {milestone.trigger_condition && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  触发条件：{milestone.trigger_condition}
                </Text>
              )}

              {milestone.notes && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  备注：{milestone.notes}
                </Text>
              )}
            </Space>
          </Timeline.Item>
        );
      });
  };

  // 计算总金额
  const totalAmount = milestones.reduce((sum, m) => sum + Number(m.payment_amount), 0);
  const isAmountMismatch = Math.abs(totalAmount - contractAmount) > 0.01;

  return (
    <Card 
      title="付费节点进度" 
      style={{ marginBottom: 24 }}
      extra={
        canEdit && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            添加节点
          </Button>
        )
      }
    >
      {/* 金额校验提示 */}
      {isAmountMismatch && (
        <div style={{ 
          marginBottom: 16,
          padding: '12px 16px', 
          background: '#fff7e6', 
          border: '1px solid #ffd591',
          borderRadius: 4,
        }}>
          <Space>
            <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
            <Text>
              节点金额总和（¥{formatAmount(totalAmount)}）与合同金额（¥{formatAmount(contractAmount)}）不一致
            </Text>
          </Space>
        </div>
      )}

      {/* 进度统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Statistic
            title="总节点数"
            value={progress.total_milestones}
            suffix="个"
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="已完成"
            value={progress.paid_milestones}
            suffix="个"
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="待付款"
            value={progress.pending_milestones}
            suffix="个"
            valueStyle={{ color: '#faad14' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="已逾期"
            value={progress.overdue_milestones}
            suffix="个"
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Col>
      </Row>

      {/* 进度条 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <Space>
            <Text strong>整体付款进度</Text>
            <Text type="secondary">
              已收款 ¥{formatAmount(Number(progress.paid_amount))} / 总金额 ¥{formatAmount(Number(progress.total_amount))}
            </Text>
          </Space>
        </div>
        <Progress
          percent={progress.payment_progress}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
          format={(percent) => `${percent?.toFixed(2)}%`}
        />
      </div>

      {/* 金额统计 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Statistic
            title="总金额"
            value={Number(progress.total_amount)}
            precision={2}
            prefix="¥"
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="已收金额"
            value={Number(progress.paid_amount)}
            precision={2}
            prefix="¥"
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="剩余金额"
            value={Number(progress.remaining_amount)}
            precision={2}
            prefix="¥"
            valueStyle={{ color: '#faad14' }}
          />
        </Col>
      </Row>

      {/* 节点时间线 */}
      {milestones.length > 0 ? (
        <div>
          <Text strong style={{ marginBottom: 16, display: 'block' }}>
            节点时间线
          </Text>
          <Timeline mode="left">
            {renderTimelineItems()}
          </Timeline>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            尚未添加付费节点
          </Text>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
            >
              添加第一个节点
            </Button>
          )}
        </div>
      )}

      {/* 添加/编辑节点弹窗 */}
      <Modal
        title={editingMilestone ? '编辑付费节点' : '添加付费节点'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={loading}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            milestone_order: milestones.length + 1,
          }}
        >
          <Form.Item
            label="节点名称"
            name="milestone_name"
            rules={[{ required: true, message: '请输入节点名称' }]}
          >
            <Input placeholder="例如：签约阶段、立案阶段、一审阶段" maxLength={100} />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              label="节点顺序"
              name="milestone_order"
              rules={[{ required: true, message: '请输入节点顺序' }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber min={1} placeholder="顺序" style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              label="应收金额"
              name="payment_amount"
              rules={[{ required: true, message: '请输入应收金额' }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                precision={2}
                placeholder="应收金额"
                addonBefore="¥"
                style={{ width: 200 }}
              />
            </Form.Item>
          </Space>

          <Form.Item
            label="触发条件"
            name="trigger_condition"
            tooltip="触发该节点付款的条件，例如：签订合同后、法院立案后等"
          >
            <Input placeholder="请输入触发条件" maxLength={200} />
          </Form.Item>

          <Form.Item
            label="应付日期"
            name="due_date"
            tooltip="预计的付款日期"
          >
            <DatePicker style={{ width: '100%' }} placeholder="选择应付日期" />
          </Form.Item>

          <Form.Item label="备注" name="notes">
            <TextArea
              rows={3}
              placeholder="请输入备注"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LeadMilestoneProgress;
